import { GoogleGenAI } from '@google/genai';

const MAX_TOOL_ITERATIONS = 5;

const SYSTEM_PROMPT = `Eres un asistente de IA integrado en "Mat ByB", un sistema de gestión de materiales y obras para una empresa constructora.

CAPACIDADES:
- Puedes consultar la base de datos de obras, tareas y documentos usando la herramienta "consultarBaseDeDatos".
- Puedes agregar nuevas tareas con "agregarTarea".
- Puedes completar tareas existentes con "completarTarea".
- Puedes abrir la sección de documentos con "abrirDocumentos".
- Puedes navegar a cualquier sección de la app con "navegarSeccion".

REGLAS:
1. Responde SIEMPRE en español.
2. Sé cordial, profesional y conciso.
3. Cuando el usuario pregunte sobre datos (obras, tareas, documentos), USA las herramientas disponibles — no inventes información.
4. Cuando presentes listas de tareas, formatea la información de forma clara y legible.
5. Si una operación falla, explica el error de forma amigable al usuario.
6. No envíes múltiples function calls del mismo tipo si una sola es suficiente.`;

class AIAssistantService {
  private ai: GoogleGenAI | null = null;
  private chatSession: any = null;
  private currentTools: any[] | undefined;
  private modelName = 'gemini-3-flash-preview';

  constructor() {
    this.initializeGenAI();
  }

  private initializeGenAI() {
    const apiKey = localStorage.getItem('gemini_api_key');
    if (apiKey) {
      this.ai = new GoogleGenAI({ apiKey });
    } else {
      this.ai = null;
      this.chatSession = null;
    }
  }

  public initialize() {
    this.initializeGenAI();
    this.chatSession = null;
  }

  public isConfigured(): boolean {
    return this.ai !== null;
  }

  public hasChatSession(): boolean {
    return this.chatSession !== null;
  }

  public startChat(history: any[] = [], tools?: any[]) {
    if (!this.ai) return;

    if (tools) {
      this.currentTools = tools;
    }

    this.chatSession = this.ai.chats.create({
      model: this.modelName,
      history: [...history],
      config: {
        systemInstruction: SYSTEM_PROMPT,
        tools: this.currentTools,
        maxOutputTokens: 1000,
      },
    });
  }

  public async sendMessage(
    message: string,
    toolDispatcher?: (call: any) => Promise<any>,
    onToolActivity?: (toolName: string) => void
  ): Promise<string> {
    if (!this.chatSession) {
      this.initializeGenAI();
      if (this.ai && !this.chatSession) {
        this.startChat();
      }
      if (!this.chatSession) {
        throw new Error('API Key no configurada. Por favor, ingresa tu clave de Gemini en Configuración.');
      }
    }

    try {
      let result = await this.chatSession.sendMessage({ message });
      
      let iterations = 0;
      while (iterations < MAX_TOOL_ITERATIONS) {
        // En el nuevo SDK, functionCalls es una propiedad directa de la respuesta
        const functionCalls = result.functionCalls;
        if (!functionCalls || functionCalls.length === 0 || !toolDispatcher) {
          break;
        }

        iterations++;

        const functionResponses = await Promise.all(
          functionCalls.map(async (call: any) => {
            if (onToolActivity) {
              onToolActivity(call.name);
            }
            try {
              const apiResponse = await toolDispatcher(call);
              return {
                functionResponse: {
                  name: call.name,
                  response: apiResponse,
                  id: call.id
                }
              };
            } catch (error: any) {
              return {
                functionResponse: {
                  name: call.name,
                  response: { error: error.message || 'Error desconocido al ejecutar herramienta' },
                  id: call.id
                }
              };
            }
          })
        );

        // En el nuevo SDK, las respuestas de funciones se envían como array de parts en `message`
        result = await this.chatSession.sendMessage({
          message: functionResponses
        });
      }

      return result.text;
    } catch (error: any) {
      console.error('Error al enviar mensaje a Gemini:', error);
      if (error?.message?.includes('API key') || error?.status === 401 || error?.status === 403) {
        this.initialize();
      }
      throw error;
    }
  }

  public async generateContent(prompt: string): Promise<string> {
    if (!this.ai) {
      throw new Error('API Key no configurada. Por favor, ingresa tu clave de Gemini en Configuración.');
    }

    try {
      const result = await this.ai.models.generateContent({
        model: this.modelName,
        contents: prompt,
        config: {
          systemInstruction: SYSTEM_PROMPT,
        }
      });
      return result.candidates?.[0]?.content?.parts?.[0]?.text || '';
    } catch (error) {
      console.error('Error al generar contenido con Gemini:', error);
      throw error;
    }
  }

  public async analyzePresupuestoImage(
    base64Data: string,
    mimeType: string
  ): Promise<{ nombre: string; unidadMedida: string; cantidad: number; precioUnitario: number; rubro: string }[]> {
    if (!this.ai) {
      throw new Error('API Key no configurada. Por favor, ingresa tu clave de Gemini en Configuración.');
    }

    const prompt = `Eres un experto en presupuestos de construcción. Analiza esta imagen y extrae TODOS los ítems del presupuesto que puedas identificar, agrupándolos por su rubro o categoría (ej: "Mampostería", "Instalación Eléctrica", "Pintura").

Para cada ítem, devuelve un objeto JSON con los campos:
- "rubro": nombre del rubro o sección donde se encuentra el ítem (string)
- "nombre": descripción del ítem (string)
- "unidadMedida": unidad de medida (string, ej: m2, m3, ml, kg, gl, u, jgo)
- "cantidad": cantidad numérica (number, usa 0 si no se puede determinar)
- "precioUnitario": precio unitario (number, usa 0 si no se puede determinar)

IMPORTANTE:
- Responde ÚNICAMENTE con un array JSON válido, sin markdown, sin texto explicativo.
- Si un ítem no parece tener un rubro claro, asígnale uno genérico o déjalo vacío.
- Si no encuentras información de algún campo, deja el valor en 0 o string vacío.
- Si no puedes identificar ningún ítem, devuelve: []

Ejemplo de respuesta válida:
[{"rubro":"Estructura","nombre":"Hormigón H21","unidadMedida":"m3","cantidad":10,"precioUnitario":85000},{"rubro":"Estructura","nombre":"Hierro Ø12mm","unidadMedida":"kg","cantidad":250,"precioUnitario":1200},{"rubro":"Albañilería","nombre":"Mampostería ladrillo hueco 12cm","unidadMedida":"m2","cantidad":45,"precioUnitario":1500}]`;

    try {
      const result = await this.ai.models.generateContent({
        model: this.modelName,
        contents: [
          {
            role: 'user',
            parts: [
              { inlineData: { mimeType, data: base64Data } },
              { text: prompt }
            ]
          }
        ]
      });

      const text = result.candidates?.[0]?.content?.parts?.[0]?.text || '[]';
      // Clean potential markdown code blocks
      const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      const parsed = JSON.parse(cleaned);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error al analizar imagen con Gemini:', error);
      throw error;
    }
  }
}

export const aiAssistantService = new AIAssistantService();
